import React, { useState } from 'react'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'
import Router from 'next/router'
import moment from 'moment'

import { getPetPhotoByType, formatPrice } from 'src/utils'
import WarningModal from 'components/warningModal'
import AvatarPhoto from '../avatarPhoto'
import Highlight from '../highlight'
import { Card } from '../forms'
import Button from '../button'

import withRootContext from 'context/withRootContextHOC'
import { useServiceScheduleContext, actionTypes } from '../../context/serviceScheduleContext'
import { cancelScheduling, getReservePay } from 'services/schedulingService'

import { ICONS, PARTNER_INFO } from './constants'

import { SCHEDULING_STATUSES } from 'src/utils/schedulingConstants'
import { assetPrefix } from 'config/env_config'
import MODAL from '../../utils/modals'
import { PAGE_URL } from 'src/utils'

import * as S from './styles'

const SchedulingCard = ({ context, schedulingInfo, id: key, marginBottom }) => {
  const {
    pet,
    petPhoto,
    day,
    time,
    professionalName,
    professionalCNPJ,
    partner,
    partnerPhone,
    payStatus,
    obs,
    externalCoupon,
    schedulingCoupon,
    campaign,
    status,
    service,
    rated,
    handleSchedulingCanceledModal,
    handleFilterSelectSchedulings,
    filterStatus,
    hasOnlinePayment
  } = schedulingInfo

  const { functions: schedulingFunctions, dispatch } = useServiceScheduleContext()

  const {
    handleEditExternalScheduling
  } = schedulingFunctions

  const [expanded, setExpanded] = useState(false)
  const [modal, setModal] = useState(undefined)
  const { name: partnerName, address: partnerAddress } = PARTNER_INFO(partner)

  const satisfactionSurveyUrl = rated
    ? `${PAGE_URL.BASE_URL}${PAGE_URL.SATISFACTION_SURVEY_DETAIL}/${key}`
    : `${PAGE_URL.BASE_URL}${PAGE_URL.SATISFACTION_SURVEY}/${key}`

  const redirectToSurvey = () => {
    window.open(satisfactionSurveyUrl, '_blank')
  }

  var dateFromObjectId = function (objectId) {
    return new Date(parseInt(objectId.substring(0, 8), 16) * 1000)
  }

  const handleSchedulingEditModal = (hasOnlinePayment = false) => {
    const modalConfig = {
      type: 'warning',
      title: hasOnlinePayment ? 'Alterações no serviço agendado' : 'Gostaria de alterar este agendamento?',
      subtitle: hasOnlinePayment ? 'Ao alterar dados do agendamento, os valores já pagos podem ter ajustes. Neste caso, ao concluir, cancelamos o pagamento anterior e geramos um novo.' : undefined,
      icon: `${assetPrefix}/static/icons/warningIcon.svg`,
      onCancel: handleWarningModalClose,
      onConfirm: handleSchedulingEdit,
      cancelLabel: 'Agora não',
      confirmLabel: 'Alterar Agendamento',
      fullButton: true,
      outlineCancelButton: true
    }

    setModal(modalConfig)
  }

  const handleSchedulingEdit = async (id) => {
    setModal(undefined)
    if (payStatus && id && payStatus === 'PAYMENT_PENDING') {
      window.localStorage.setItem('reservePay', JSON.stringify({
        id,
        expiresAt: moment(dateFromObjectId(id)).add(process.env.SCHEDULING_TTL, 'minutes').format('YYYY-MM-DD HH:mm')
      }))
      Router.push({
        pathname: `${PAGE_URL.PAYMENT_SUMMARY}/${key}`
      })
    } else {
      try {
        context.showLoader()

        const newState = await handleEditExternalScheduling({ context }, schedulingInfo)

        window.localStorage.removeItem('schedulingState')
        window.localStorage.removeItem('paymentState')

        dispatch({
          type: actionTypes.UPDATE_STATE,
          payload: newState
        }, () => {
          Router.push({
            pathname: PAGE_URL.SCHEDULING_REVIEW,
            query: {
              category: service.category,
              id: key,
              reschedule: true
            }
          })
        })

        context.hideLoader()
      } catch (err) {
        window.localStorage.removeItem('schedulingState')
        window.localStorage.removeItem('paymentState')
        Router.push({ pathname: PAGE_URL.PAGE_NOT_FOUND })
      }
    }
  }

  const handleRepeatSchedulingEdit = async () => {
    try {
      context.showLoader()

      const withoutDate = true

      const newState = await handleEditExternalScheduling({ context }, schedulingInfo, withoutDate)

      window.localStorage.removeItem('schedulingState')
      window.localStorage.removeItem('paymentState')

      if (newState?.reeschedulingError) {
        const stepToRedirect = {
          1: PAGE_URL.SCHEDULING_PET_AND_SERVICES,
          2: PAGE_URL.SCHEDULING_LOCATION,
          4: PAGE_URL.SCHEDULING_PROFESSIONAL
        }

        setModal(MODAL.STEP_UPDATE(
          () => {
            dispatch({
              type: actionTypes.UPDATE_STATE,
              payload: newState
            }, () => {
              Router.push({
                pathname: stepToRedirect?.[newState?.activeStep],
                query: {
                  id: key,
                  category: service.category
                }
              })
            })

            setModal(undefined)
          },
          () => {
            Router.push({
              pathname: PAGE_URL.SCHEDULING_INDEX
            })

            setModal(undefined)
          }
        ))

        context.hideLoader()

        return
      }

      setModal(MODAL.REPEAT_SCHEDULE_DATE_SELECTION(
        () => {
          window.localStorage.removeItem('schedulingState')
          window.localStorage.removeItem('paymentState')

          dispatch({
            type: actionTypes.UPDATE_STATE,
            payload: newState
          }, () => {
            Router.push({
              pathname: PAGE_URL.SCHEDULING_DATE,
              query: {
                id: key,
                category: service.category
              }
            })
          })

          setModal(undefined)
        },
        () => {
          Router.push({
            pathname: PAGE_URL.SCHEDULING_INDEX
          })

          setModal(undefined)
        }, {
          service: schedulingInfo?.service?.name,
          partner: schedulingInfo?.partner?.split('-')?.[0],
          professional: schedulingInfo?.professionalName
        }
      ))

      context.hideLoader()
    } catch (err) {
      console.error(err)
      window.localStorage.removeItem('schedulingState')
      window.localStorage.removeItem('paymentState')
      toast.error('Ocorreu um erro, tente novamente')
    }
  }

  const handleCancelScheduling = async () => {
    setModal(undefined)
    context.showLoader()
    cancelScheduling(key)
      .then(() => {
        handleCancelRedirect()
        context.hideLoader()
      })
      .catch(() => {
        setModal({
          type: 'warning',
          title: 'Não foi possível cancelar seu agendamento',
          subtitle: 'Entre em contato com a recepção e solicite o cancelamento.',
          icon: `${assetPrefix}/static/icons/red-exclamation.svg`,
          onConfirm: handleWarningModalClose,
          confirmLabel: 'Fechar',
          fullButton: true
        })
        context.hideLoader()
      })
  }

  const handleCancelSchedulingModal = (hasOnlinePayment = false) => {
    const modalConfig = {
      type: 'warning',
      title: 'Cancelar serviço agendado',
      icon: `${assetPrefix}/static/icons/warningIcon.svg`,
      subtitle: `${hasOnlinePayment ? 'Não se preocupe, ao cancelar o serviço, nenhum valor será cobrado em seu cartão.' : ''} Gostaria de cancelar este agendamento? `,
      outlineCancelButton: true,
      onCancel: handleWarningModalClose,
      onConfirm: handleCancelScheduling,
      cancelLabel: 'Agora não',
      confirmLabel: 'Sim, cancelar'
    }

    setModal(modalConfig)
  }

  const handleCancelRedirect = () => {
    handleFilterSelectSchedulings(filterStatus)
    handleSchedulingCanceledModal()
  }

  const handleWarningModalClose = () => {
    setModal(undefined)
  }

  const handlePaySchedulingModal = () => {
    const modalConfig = {
      type: 'danger',
      title: 'O tempo expirou',
      icon: `${assetPrefix}/static/icons/red-exclamation.svg`,
      subtitle: 'A reserva temporária do profissional, data e horário expirou. Clique em "Novo agendamento” para fazer uma nova escolha.',
      outlineCancelButton: true,
      onConfirm: handleRedirectToHome,
      onCancel: handleWarningModalClose,
      confirmLabel: 'Novo agendamento',
    }

    setModal(modalConfig)
  }

  const handleRedirectToHome = () => {
    Router.push(PAGE_URL.SCHEDULING_INDEX)
  }

  const handleSchedulingPay = (id) => {
    const reservePay = getReservePay()
    // Verifica se a reserva existe e se o tempo expirou
    const hasTimeExpired = !reservePay || !reservePay.expiresAt

    if (hasTimeExpired) {
      handlePaySchedulingModal()
    } else if (reservePay.id === id) {
      Router.push(PAGE_URL.CHECKOUT_ADDRESS)
    }
  }

  return (
    <Card key={key} active data-test={`scheduling-${key}`} marginBottom={marginBottom}>
      <S.SchedulingInfoWrapper expanded={expanded} onClick={() => setExpanded(!expanded)}>
        <AvatarPhoto src={petPhoto || getPetPhotoByType(pet?.breed?.type)} contain={!petPhoto} />
        <section>
          <S.SchedulingCardTitle>
            <div>
              <h2>{service.name}</h2>
              <p>{day} &#x2022; {time}</p>
            </div>
            <Highlight type={SCHEDULING_STATUSES[status].role}>{SCHEDULING_STATUSES[status].message}</Highlight>
          </S.SchedulingCardTitle>
        </section>
        <S.Chevron expanded={expanded} />
      </S.SchedulingInfoWrapper>
      {
        expanded && (
          <>
            <S.SchedulingInfoWrapper>
              <S.ImageWrapper><img src={ICONS.service(service.name)} alt='Serviço agendado' /></S.ImageWrapper>
              <h2>{service.name}</h2>
            </S.SchedulingInfoWrapper>
            <S.SchedulingInfoWrapper>
              <S.ImageWrapper><img src={ICONS.pet} alt='Pet que será atendido' /></S.ImageWrapper>
              <h2>{pet.name}</h2>
            </S.SchedulingInfoWrapper>
            <S.SchedulingInfoWrapper>
              <S.ImageWrapper><img src={ICONS.location} alt='Loja' /></S.ImageWrapper>
              <h2>{partnerName}</h2>
              <S.Description>{partnerAddress}</S.Description>
            </S.SchedulingInfoWrapper>
            <S.SchedulingInfoWrapper>
              <S.ImageWrapper><img src={ICONS.phone} alt='Telefone da loja' /></S.ImageWrapper>
              <h2>{partnerPhone}</h2>
            </S.SchedulingInfoWrapper>
            <S.SchedulingInfoWrapper>
              <S.ImageWrapper><img src={ICONS.professional} alt='Profissional que realizará o atendimento' /></S.ImageWrapper>
              <h2>{professionalName}</h2>
              <S.Description>CNPJ {professionalCNPJ}</S.Description>
            </S.SchedulingInfoWrapper>
            {obs &&
              <S.SchedulingInfoWrapper>
                <S.ImageWrapper><img src={ICONS.obs} alt='Observação' /></S.ImageWrapper>
                <h2>Observação</h2>
                <S.Description>{obs}</S.Description>
              </S.SchedulingInfoWrapper>}
            {campaign &&
              <S.SchedulingInfoWrapper>
                <S.ImageWrapper><img src={ICONS.campaign} alt='Campanha' /></S.ImageWrapper>
                <h2>Campanha</h2>
                <S.Description>Desconto de {campaign.discountType === 'PERCENTAGE' ? `${campaign.discountValue}%` : formatPrice(campaign.discountValue)} por agendar em período promocional</S.Description>
              </S.SchedulingInfoWrapper>}
            {(externalCoupon || schedulingCoupon) &&
              <S.SchedulingInfoWrapper>
                <S.ImageWrapper><img src={ICONS.coupon} alt='Cupom' /></S.ImageWrapper>
                <h2>Cupom</h2>
                <S.Description>
                  {
                    externalCoupon && (
                      status === 'FINISHED' ? coupon.code.toUpperCase() : 'Ao realizar este serviço você receberá um cupom de desconto para ser utilizado na loja virtual Cobasi.'
                    )
                  }
                  {
                    schedulingCoupon && schedulingCoupon.code
                  }
                </S.Description>
              </S.SchedulingInfoWrapper>}
            {
              status === 'SCHEDULED' &&
                <S.SchedulingInfoWrapper>
                  <Button onClick={() => handleCancelSchedulingModal(hasOnlinePayment)} label='Cancelar agendamento' margin='0 0 16px 0' theme='secondary' />
                  <Button onClick={() => handleSchedulingEditModal(hasOnlinePayment)} label='Editar agendamento' margin='0' />
                </S.SchedulingInfoWrapper>
            }
            {
              status === 'PAYMENT_PENDING' &&
                <S.SchedulingInfoWrapper>
                  <Button onClick={() => handleCancelSchedulingModal(hasOnlinePayment)} label='Cancelar agendamento' margin='0 0 16px 0' theme='secondary' />
                  <Button onClick={() => handleSchedulingPay(key)} label='Realizar pagamento' margin='0' />
                </S.SchedulingInfoWrapper>
            }
            {
              status === 'FINISHED' &&
                <S.SchedulingInfoWrapper>
                  <Button onClick={redirectToSurvey} label={rated ? 'Avaliação do agendamento' : 'Avaliar agendamento'} margin='0 0 16px 0' theme='secondary' />
                  <Button onClick={handleRepeatSchedulingEdit} label='Repetir agendamento' margin='0' />
                </S.SchedulingInfoWrapper>
            }
          </>
        )
      }
      <WarningModal
        visible={!!modal}
        type={modal?.type}
        title={modal?.title}
        subtitle={modal?.subtitle}
        onCancel={modal?.onCancel}
        onConfirm={modal?.onConfirm}
        icon={modal?.icon}
        cancelLabel={modal?.cancelLabel}
        confirmLabel={modal?.confirmLabel}
        fullButton={modal?.fullButton}
        outlineCancelButton={modal?.outlineCancelButton}
        dataTest='modal-warning-scheduling'
      >
        {modal?.body}
      </WarningModal>
    </Card>
  )
}

SchedulingCard.proptypes = {
  schedulingInfo: PropTypes.object,
  isDisabled: PropTypes.bool
}

export default withRootContext(SchedulingCard)
